import React, { useCallback, useContext, useState } from "react";
import { Tooltip, OverlayTrigger, Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faWindowRestore } from "@fortawesome/free-solid-svg-icons";
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import { DEFAULT_DELAY, NotificationContext } from "@components/Notification/Notification";
import { ToDoCheckIcon } from "@assets/icons";
import Checkbox from "ui/Checkbox";
import { ga4PackagesExtraGTM } from "@helper/gtm";
import ResponsiveImage from "ui/Image";
import ImageSlider from "@components/ImageSlider";
import { IMAGE_SOURCES } from "@helper/constants";
import { VwpOrderCategory, VwpOrderExtra, VwpPackageItemExtra } from "@components/WeddingSupplierPackages/types";
import { useVwp } from "@components/WeddingSupplierPackages/VwpContext";
import { formatExtraLabel } from "../helpers";

import css from './SupplierExtras.module.scss'

export function Notification({ onClose, children }: {onClose: () => void, children: React.ReactNode}) {
    return (<>
        <ToDoCheckIcon className={css.checkIcon}/>
        <div style={{flexGrow: 1}}>
            {children}
        </div>
        
        <Button variant={'outline'} className={`p-0`} onClick={onClose}>
            <div className={css.closeIcon}>✕</div>
        </Button>
    </>)
}

function Counter({ quantity, onIncrease, onDecrease }: {quantity: number, onIncrease: () => void, onDecrease: () => void}) {
    return (<div className={css.counter}>
        <button className={css.roundButton} onClick={onDecrease}>
            <Icon icon={faMinus} style={{maxWidth: '2rem', maxHeight: '2rem'}}/>
        </button>
        <span>{quantity}</span>
        <button className={css.roundButton} onClick={onIncrease}>
            <Icon icon={faPlus} style={{maxWidth: '2rem', maxHeight: '2rem'}}/>
        </button>
    </div>)
}

type ExtraProps = {
    extra: VwpPackageItemExtra;
    packageOrderExtra: VwpOrderExtra | undefined;
    packageName: string;
    supplierName: string;
    changeExtra: (extraId: number, value: number) => void;
}

function Extra({ extra, packageOrderExtra, packageName, supplierName, changeExtra }: ExtraProps) {
    const { enableGTMTracking } = useVwp();
    const [show, setShow] = useState(false);
    const [content, setContent] = useState('');

    const handleClose = () => setShow(false);
    const handleShow = (info: string) => {
        setContent(info);
        setShow(true);
    }

    const priceField = 'rrpPrice';
    const {setNotice, setDelay, onClose} = useContext(NotificationContext);

    const onChange = (e: any) => {
        const isAdd = (e.target.checked ? 1 : 0);
        changeExtra(extra.id, isAdd);
        const action = e.target.checked ? 'added' : 'removed';
        setDelay(DEFAULT_DELAY);
        setNotice(<Notification onClose={onClose}>You've {action} {formatExtraLabel(extra, priceField, isSingle, e.target.checked)}</Notification>)
        ga4PackagesExtraGTM(packageName, (isAdd ? 'added' : 'deleted'), supplierName, extra.description, null, enableGTMTracking);
    }

    const onDecrease = useCallback(() => {
        if(!packageOrderExtra) return
        const count = packageOrderExtra.quantity - 1;
        changeExtra(extra.id, count);
        setDelay(DEFAULT_DELAY);
        setNotice(<Notification onClose={onClose}>You've removed {formatExtraLabel(extra, priceField, isSingle, false)}</Notification>)
        ga4PackagesExtraGTM(packageName, 'decrease', supplierName, extra.description, count, enableGTMTracking);
    }, [extra.id, packageOrderExtra?.quantity])

    const onIncrease = useCallback(() => {
        const count = (packageOrderExtra?.quantity ?? 0) + 1;
        changeExtra(extra.id, count);
        setDelay(DEFAULT_DELAY);
        setNotice(<Notification onClose={onClose}>You've added {formatExtraLabel(extra, priceField, isSingle)}</Notification>)
        ga4PackagesExtraGTM(packageName, 'increase', supplierName, extra.description, count, enableGTMTracking);
    }, [extra.id, packageOrderExtra?.quantity])
    const isSingle = extra.extraTypeId === 1;

    const tooltip = !!extra.tooltip ? (
        (extra.isEnhancedTooltip ?
            <Button size={'sm'} variant={'link'} className={css.infoButton} onClick={() => handleShow(extra.tooltip)}>
                <Icon icon={faWindowRestore} style={{ maxWidth: '2rem', maxHeight: '2rem' }} />
            </Button>
            :
            <OverlayTrigger placement="top" overlay={props => <Tooltip {...props}>{extra.tooltip}</Tooltip>}>
                <Icon icon={faInfoCircle} style={{maxWidth: '2rem', maxHeight: '2rem'}}/>
            </OverlayTrigger>
        )        
    ) : null;

    const images = (extra.isEnhancedTooltip && !!extra.tooltipImages) ? extra.tooltipImages.map(img => {
        return {
            src: img,
            title: '',
        };
    }) : [];

    const modal = extra.isEnhancedTooltip ? (
        <Modal show={show} onHide={handleClose} size={'lg'}>
            <Modal.Header closeButton>
                <Modal.Title>{formatExtraLabel(extra, priceField, isSingle)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {images.length > 0 && <div className={css.imagesContainer}>
                    {images.length === 1 ?
                        <ResponsiveImage
                            src={images[0].src}
                            sources={IMAGE_SOURCES}
                            className={`${css.image} rounded`}
                            onDragStart={e => e.preventDefault()}
                            loading="lazy"
                        />
                    : <ImageSlider
                            lazy
                            isHiddenControls={false}
                            images={images}
                            sources={IMAGE_SOURCES}
                        />}
                </div>}                
                <div className={images.length > 0 ? 'mt-4' : undefined} dangerouslySetInnerHTML={{ __html: content }} />
            </Modal.Body>
        </Modal>
    ) : null;

    if(isSingle) {
        return (<div className="d-flex">
            <Checkbox onClick={onChange} isChecked={!!packageOrderExtra && packageOrderExtra?.quantity > 0} className={css.extra}>
                <span>
                    {formatExtraLabel(extra, priceField, isSingle)}
                </span>
            </Checkbox>
            <div className="ms-2">
                {tooltip}
                {modal}
            </div>
        </div>)
    }

    return (<label className={css.extra}>
        <Counter
            quantity={packageOrderExtra?.quantity ?? 0}
            onIncrease={onIncrease}
            onDecrease={onDecrease}
            />
        <span>
            {formatExtraLabel(extra, priceField, isSingle)}
        </span>
        {tooltip}
        {modal}
    </label>)
}

type Props = {
    extras: VwpPackageItemExtra[]
    packageOrder: VwpOrderCategory | undefined,
    packageName: string;
    supplierName: string;
    changeExtra: (extraId: number, value: number) => void;
}

function SupplierPackageExtras({ extras, packageOrder, packageName, supplierName, changeExtra }: Props) {
    return (<>
        {extras.map((extra) => (
            <Extra
                key={extra.id}
                extra={extra}
                packageName={packageName}
                supplierName={supplierName}
                packageOrderExtra={packageOrder?.extras.find(e => e.extraId === extra.id) as VwpOrderExtra}
                changeExtra={changeExtra}
            />
        ))}
    </>)
}

export default SupplierPackageExtras;